import { useState } from 'react';
import { webRoutes } from '@runhuman/shared';
import { useTesterQueue } from '../use-tester-queue';
import { useActiveJob } from '../use-active-job';
import { useTesterPresence } from '../use-tester-presence';
import { useActivityConfig } from '../use-activity-config';
import { useFloorManagerOnDuty } from '../use-floor-manager-on-duty';
import { QueueList } from './tester/QueueList';
import { ActiveJobPanel } from './tester/ActiveJobPanel';
import { PresenceRoster } from './tester/PresenceRoster';
import { InternalTesterProfileDialog } from './tester/profile-modal/InternalTesterProfileDialog';
import { openInBrowser } from './tester/harness';
import { FloorManagerOnDutyTile } from './FloorManagerOnDutyTile';
import { AdminStuckJobsSection } from './admin/AdminStuckJobsSection';
import { AdminPendingBreakRequestsSection } from './admin/AdminPendingBreakRequestsSection';
import { WebPopoutButton } from './WebPopoutButton';
import { getDiscordSdk } from '../sdk-client';
import { TESTER_SCHEDULE_URL } from '../external-urls';
import { claimJob } from '../claim';
import { ExternalLinkIcon } from '../ExternalLinkIcon';
import type {
  DiscordActivityCapabilities,
  TesterPresenceItem,
  TesterQueueJob,
} from '@runhuman/shared';

interface Props {
  readonly discordUserId: string;
  readonly accessToken: string;
  /**
   * Server-derived capability flags. `canClaim` gates the Claim button (non-
   * tester admins see the queue but the Claim button is disabled with a
   * reason tooltip); `isAdmin` layers in the inline admin widgets (header
   * "Open jobs in admin" action + stuck-jobs alert strip) on top of the
   * shared layout below.
   */
  readonly capabilities: DiscordActivityCapabilities;
}

const NON_TESTER_CLAIM_REASON =
  "You're not a tester — only testers can claim jobs.";

/**
 * Single consolidated Activity view for every linked user (#4172). Replaces
 * the older Tester Lounge ↔ Ops Console flip — admins now land here directly,
 * with the FM tile pinned up top for everyone and admin-only widgets layered
 * inline rather than hidden behind a secondary surface.
 *
 * React Query subscriptions:
 *   - `useActiveJob`           → the user's currently-claimed job, if any
 *   - `useTesterQueue`         → available + in-progress + late-submission
 *   - `useTesterPresence`      → global active-tester roster (parity w/ /tester)
 *   - `useFloorManagerOnDuty`  → who's wearing the crown (everyone)
 *   - `useAdminOpsSnapshot`    → admin-only stuck-jobs (inside AdminStuckJobsSection)
 *
 * Layout, top to bottom:
 *   Header (Schedule + admin-only "Open jobs in admin")
 *   FM tile  (everyone — answers "who's on point right now?")
 *   Stuck-jobs strip  (admins only, only when there are stuck jobs)
 *   Active job panel  (when claimed)
 *   Queue
 *   Presence roster
 *
 * Non-tester admins see the queue but can't claim — the Claim button surfaces
 * a disabled tooltip ("You're not a tester — only testers can claim jobs"),
 * which is friendlier than a silent no-op. The gate stays server-authoritative.
 *
 * "Schedule" hands off directly to `runhuman.com/tester/schedule` via the
 * Discord SDK's `openExternalLink` — the iframe is too cramped for any
 * non-trivial schedule UI.
 */
export function TesterView({
  discordUserId: _discordUserId,
  accessToken,
  capabilities,
}: Props) {
  const [openProfile, setOpenProfile] = useState<TesterPresenceItem | null>(null);
  const queueQuery = useTesterQueue(accessToken);
  const activeJobQuery = useActiveJob(accessToken);
  const presenceQuery = useTesterPresence(accessToken);
  const fmOnDutyQuery = useFloorManagerOnDuty(accessToken);
  const { data: timing } = useActivityConfig(accessToken);

  const handleClaim = async (job: TesterQueueJob) => {
    const { webTestLink } = await claimJob(job.id, accessToken);
    await openInBrowser(webTestLink);
  };

  // Admins clicking a tester chip open the Main / About / History modal.
  // Non-admins receive `undefined` so rows render inert, mirroring the web
  // `<TesterProfileTrigger>`.
  const handleTesterClick = capabilities.isAdmin
    ? (tester: TesterPresenceItem) => setOpenProfile(tester)
    : undefined;

  const handleOpenSchedule = () => {
    void getDiscordSdk().commands.openExternalLink({ url: TESTER_SCHEDULE_URL });
  };

  if (queueQuery.isLoading || activeJobQuery.isLoading) {
    return <div className="view view--loading">Loading queue…</div>;
  }

  if (queueQuery.isError || activeJobQuery.isError) {
    const err = queueQuery.error ?? activeJobQuery.error;
    return (
      <div className="view view--error">
        <h1>Couldn't load your queue</h1>
        <p>{err instanceof Error ? err.message : 'Unknown error'}</p>
      </div>
    );
  }

  const activeJob = activeJobQuery.data?.activeJob;
  const queue = queueQuery.data!;
  const presence = presenceQuery.data;
  const fmPayload = fmOnDutyQuery.data;
  const claimDisabledReason = capabilities.canClaim ? null : NON_TESTER_CLAIM_REASON;

  return (
    <div className="view view--tester">
      <header className="view__header">
        <h1>Runhuman Tester Lounge</h1>
        <div className="view__header-actions">
          <button
            type="button"
            className="view__header-action"
            onClick={handleOpenSchedule}
          >
            Schedule
            <ExternalLinkIcon />
          </button>
          {capabilities.isAdmin && (
            <WebPopoutButton path={webRoutes.adminJobs.build()}>
              Open jobs in admin
            </WebPopoutButton>
          )}
        </div>
      </header>
      {fmPayload && <FloorManagerOnDutyTile floorManagersOnDuty={fmPayload.onDuty} />}
      {capabilities.isAdmin && <AdminStuckJobsSection accessToken={accessToken} />}
      {capabilities.isAdmin && <AdminPendingBreakRequestsSection accessToken={accessToken} />}
      {activeJob && <ActiveJobPanel activeJob={activeJob} />}
      <QueueList
        queue={queue}
        onClaim={handleClaim}
        disabled={Boolean(activeJob)}
        claimDisabledReason={claimDisabledReason}
        isAdmin={capabilities.isAdmin}
      />
      {presence && (
        <PresenceRoster
          testers={presence.testers}
          timing={timing}
          onTesterClick={handleTesterClick}
          showAdminLink={capabilities.isAdmin}
        />
      )}
      <InternalTesterProfileDialog
        tester={openProfile}
        accessToken={accessToken}
        isAdmin={capabilities.isAdmin}
        onClose={() => setOpenProfile(null)}
      />
    </div>
  );
}
